import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import axios from "axios";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { openRazorpayCheckout } from "../../lib/razorpay";

const API = process.env.REACT_APP_BACKEND_URL;

const L = "block text-xs tracking-[0.16em] uppercase text-slate-400 mb-2";
const IN = "w-full bg-white/[0.04] border border-white/15 focus:border-[#C9A84C] outline-none px-4 py-3 text-[15px] text-white placeholder:text-slate-600 transition-colors";

export default function PaymentLink() {
  const { txnid } = useParams();
  const [pay, setPay] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [rzpPublic, setRzpPublic] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/payments/config-status`).then((r) => { setRzpPublic(!!r.data.razorpay_public); }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/payments/link/${txnid}`)
      .then((r) => setPay(r.data))
      .catch(() => setNotFound(true));
  }, [txnid]);

  const start = async (gateway) => {
    setErr("");
    setBusy(gateway);
    try {
      const res = await axios.post(`${API}/api/payments/link/${txnid}/pay`, { phone, gateway });
      if (res.data.razorpay) {
        await openRazorpayCheckout({
          API, txnid,
          onPaid: () => window.location.assign(`/payment/success?txnid=${txnid}`),
          onError: (m) => { setErr(m); setBusy(""); },
        });
        return;
      }
      if (res.data.redirect_url) {
        window.location.assign(res.data.redirect_url);
        return;
      }
      const { payment_url, form } = res.data;
      // Browser POST is intentional: PayU receives the hosted-checkout form directly.
      const el = document.createElement("form");
      el.method = "POST";
      el.action = payment_url;
      Object.entries(form).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden";
        i.name = k;
        i.value = String(v ?? "");
        el.appendChild(i);
      });
      document.body.appendChild(el);
      el.submit();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not start the payment. Please try again.");
      setBusy("");
    }
  };

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased min-h-screen flex flex-col" data-testid="payment-link-page">
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center px-5 py-24">
        <div className="w-full max-w-md border border-[#C9A84C]/30 bg-[#0A1628] p-8 sm:p-10">
          {notFound ? (
            <div className="text-center">
              <h1 className="font-marcellus text-2xl text-white">Link Not Found</h1>
              <p className="text-sm text-slate-400 mt-3">This payment link is invalid or has expired. Please contact us for a new one.</p>
              <Link to="/" className="inline-block mt-6 border border-[#C9A84C]/60 text-[#C9A84C] text-sm px-8 py-3" data-testid="payment-link-home">Back to Home</Link>
            </div>
          ) : !pay ? (
            <p className="text-slate-500 text-sm text-center">Loading…</p>
          ) : pay.status === "paid" ? (
            <div className="text-center" data-testid="payment-link-paid">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-emerald-500/50 bg-emerald-500/10"><Check size={26} className="text-emerald-400" /></span>
              <h1 className="font-marcellus text-2xl text-white mt-5">Already Paid</h1>
              <p className="text-sm text-slate-400 mt-3">This invoice ({pay.reference}) has already been paid. Thank you.</p>
            </div>
          ) : (
            <>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#C9A84C]">NexSecure Technologies</p>
              <h1 className="font-marcellus text-2xl text-white mt-2" data-testid="payment-link-title">Invoice Payment</h1>
              <div className="mt-6 border border-white/10 divide-y divide-white/10" data-testid="payment-link-details">
                <div className="flex justify-between gap-6 px-5 py-3.5"><span className="text-xs tracking-[0.16em] uppercase text-slate-500">Reference</span><span className="text-sm text-white text-right">{pay.reference}</span></div>
                <div className="flex justify-between gap-6 px-5 py-3.5"><span className="text-xs tracking-[0.16em] uppercase text-slate-500">Billed To</span><span className="text-sm text-white text-right">{pay.name}</span></div>
                <div className="flex justify-between gap-6 px-5 py-3.5"><span className="text-xs tracking-[0.16em] uppercase text-slate-500">Amount</span><span className="text-lg font-bold text-[#C9A84C]" data-testid="payment-link-amount">₹{pay.amount_str}</span></div>
              </div>
              {pay.notes && <p className="text-xs text-slate-500 mt-4 leading-relaxed">{pay.notes}</p>}
              <form onSubmit={(e) => e.preventDefault()} className="mt-6 space-y-4">
                <div>
                  <label className={L} htmlFor="plink-phone">Your Mobile Number</label>
                  <input id="plink-phone" data-testid="payment-link-phone" type="tel" className={IN} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile (for payment updates)" required />
                </div>
                {err && <p className="text-[12px] text-red-400" data-testid="payment-link-error">{err}</p>}
                <div className={rzpPublic ? "grid gap-3 sm:grid-cols-2" : ""}>
                  {rzpPublic && (
                    <button type="submit" disabled={!!busy} onClick={() => start("razorpay")} className="w-full bg-[#C9A84C] hover:bg-[#dbb95c] disabled:opacity-60 text-[#0A1628] font-bold tracking-wide text-sm px-4 py-4 transition-colors" data-testid="payment-link-pay-razorpay">
                    {busy === "razorpay" ? "Opening…" : "Pay with Razorpay"}
                    </button>
                  )}
                  <button type="submit" disabled={!!busy} onClick={() => start("payu")} className="w-full bg-[#C9A84C] hover:bg-[#dbb95c] disabled:opacity-60 text-[#0A1628] font-bold tracking-wide text-sm px-4 py-4 transition-colors" data-testid="payment-link-pay-payu">
                    {busy === "payu" ? "Redirecting…" : "Pay with PayU"}
                  </button>
                </div>
                <p className="text-[11px] text-slate-600 text-center">UPI · Cards · Net Banking · Wallets — processed by Razorpay or PayU. NexSecure never sees your card details.</p>
              </form>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
